import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useShopConsignmentInvoices } from '../../api/hooks';
import { useSession } from '../../auth/session';
import type { ShopConsignmentInvoiceRow } from '../../api/types';
import { Alert, Chip, Empty, Money, Panel, Spinner, Table } from '../../ui';
import { shipmentStateLabel } from '../shipment-state';
import { SHIPMENT_TONE } from './consignment-invoice-tone';

/**
 * R-23 — every CONSIGNMENT INVOICE Miraki has raised for this account, newest first, in one place
 * beside Orders. A consignment invoice is the DESPATCH document: it lists the pieces sent on
 * consignment, title stays with Miraki (SH-06), and nothing on it is payable (the courier charge was
 * retired on 04-Nov-2026). It is NOT the purchase invoice the retailer pays — that one lives on R-22 Payables.
 *
 * Read-only: opening one shows its products, its courier payment and its shipment. Partial
 * deliveries each appear as their own consignment invoice, so an order with some pieces sent and
 * some still being made shows one row per batch.
 */
export function ShopConsignmentInvoices() {
  const { t } = useTranslation();
  const { me } = useSession();
  const location = useLocation();
  const { data, isLoading, error } = useShopConsignmentInvoices();
  const currency = me?.kind === 'retailer' ? me.retailer.tradingCurrency : undefined;
  const here = `${location.pathname}${location.search}`;
  const rows = useMemo(() => data ?? [], [data]);

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{t('shop.consignmentInvoice.loadFailed')}</Alert>;

  return (
    <Panel>
      <Table<ShopConsignmentInvoiceRow>
        rows={rows}
        rowKey={(r) => r.id}
        empty={<Empty title={t('shop.consignmentInvoices.emptyTitle')} body={t('shop.consignmentInvoices.emptyBody')} />}
        columns={[
          {
            header: t('shop.consignmentInvoices.colInvoice'),
            render: (r) => (
              <>
                <Link to={`/shop/consignment-invoices/${r.id}`} state={{ backTo: here }}>{r.reference}</Link>
                <div className="mk-dim">{new Date(r.createdAt).toLocaleDateString()}</div>
              </>
            ),
          },
          { header: t('shop.consignmentInvoices.colOrders'), render: (r) => r.orderReferences.join(', ') },
          /* What travelled, and nothing about money owed: the goods are consigned (SH-06) and no courier
             charge is payable either (04-Nov-2026). What the retailer owes is on the purchase invoice
             (R-22 Payables). This table used to carry the goods' value under "Total payable" and billed
             a retailer for stock they do not own. */
          {
            header: t('despatch.colConsigned'), numeric: true,
            render: (r) => <Money amount={r.consignedValue} currency={currency ?? r.currency} />,
          },
          {
            header: t('shop.consignmentInvoices.colShipment'),
            render: (r) => (r.shipment
              ? <>{r.shipment.reference} <Chip tone={SHIPMENT_TONE[r.shipment.status]}>{shipmentStateLabel(r.shipment.status)}</Chip></>
              : <span className="mk-dim">{t('shop.consignmentInvoices.noShipment')}</span>),
          },
        ]}
      />
    </Panel>
  );
}
